'use client';

import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { usePathname, useRouter } from 'next/navigation';
import { FeatureErrorFallback, handleBoundaryError } from '@/components/ui/FeatureErrorFallback';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Input } from '@/components/ui/Input';
import { useDebounce } from '@/hooks/useDebounce';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { PendingUploadsBanner } from './PendingUploadsBanner';
import { RecordingList, type RecordingView } from './RecordingList';
import { RecordingPlayer } from './RecordingPlayer';
import { RecordingUpload } from './RecordingUpload';
import { filterRecordings } from './searchRecordings';
import { ViewToggle } from './ViewToggle';
import { useRecordingsList, useRecordingsStore } from './recordingsStore';

const VIEW_KEY = 'lvdr:recordings-view';
const WIDE_KEY = 'lvdr:recordings-wide';
const WIDE_QUERY = '(min-width: 1024px)';

/**
 * Read the persisted view preference. Machine-only (survives logout like
 * theme), so a NON-`lvdr_session_` key. All localStorage access is wrapped in
 * try/catch — private-mode / disabled-storage falls back to the default.
 */
function readPersistedView(): RecordingView {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    if (v === 'cards' || v === 'rows') return v;
  } catch {
    // storage unavailable — fall back to the default.
  }
  return 'cards';
}

/**
 * Seed the `useMediaQuery` breakpoint from the last resolved value so
 * wide-screen users don't get a one-frame narrow→wide flash on load
 * (architect SF3). Defaults to `false` (narrow is the safe pre-mount layout —
 * it never double-mounts the heavy player).
 */
function readPersistedWide(): boolean {
  try {
    return window.localStorage.getItem(WIDE_KEY) === 'true';
  } catch {
    return false;
  }
}

function RecordingsPageInner({ initialId }: { initialId?: string | undefined }) {
  const t = useTranslations('recordings');
  const router = useRouter();
  const pathname = usePathname();

  const [selectedId, setSelectedId] = React.useState<string | null>(initialId ?? null);
  const [view, setView] = React.useState<RecordingView>(() => readPersistedView());
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState('');
  // Debounce so a fast typist doesn't re-filter (and re-render the list) on
  // every keystroke; 200ms is below the perception threshold for a local list.
  const query = useDebounce(searchInput, 200);

  const wide = useMediaQuery(WIDE_QUERY, readPersistedWide());

  const recordings = useRecordingsStore((s) => s.recordings);
  const list = useRecordingsList();
  const selected = selectedId ? recordings.get(selectedId) ?? null : null;
  const trimmedQuery = query.trim();
  const visibleCount = React.useMemo(
    () => (trimmedQuery ? filterRecordings(list, trimmedQuery).length : list.length),
    [list, trimmedQuery],
  );

  // Persist the resolved breakpoint so the next load can seed without a flash.
  React.useEffect(() => {
    try {
      window.localStorage.setItem(WIDE_KEY, wide ? 'true' : 'false');
    } catch {
      // storage unavailable — the in-memory value still drives this session.
    }
  }, [wide]);

  // Persist the view preference whenever it changes.
  React.useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch {
      // storage unavailable — the in-memory value still drives this session.
    }
  }, [view]);

  // Soft-nav helpers — preserve the WS singleton (never window.location).
  const select = React.useCallback(
    (id: string) => {
      setUploadOpen(false);
      setSelectedId(id);
      router.replace(`?id=${encodeURIComponent(id)}`);
    },
    [router],
  );

  const clear = React.useCallback(() => {
    setSelectedId(null);
    router.replace(pathname);
  }, [router, pathname]);

  // If the selected row gets deleted from the store, clear the selection AND
  // drop the now-stale `?id=` param (via clear()) so a refresh/share doesn't
  // re-seed a deleted recording. Declared after clear() to avoid the TDZ.
  React.useEffect(() => {
    if (selectedId && !recordings.has(selectedId)) {
      clear();
    }
  }, [selectedId, recordings, clear]);

  const closeDrawer = React.useCallback(() => {
    setUploadOpen(false);
    if (selectedId) {
      clear();
    }
  }, [selectedId, clear]);

  // Shared search box — rendered in the wide rail header and the narrow top bar.
  // Hidden until there's something to search (no point filtering an empty list).
  const searchBox =
    list.length > 0 ? (
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('recordingsPage.searchPlaceholder')}
          aria-label={t('recordingsPage.searchLabel')}
          className="h-8 pl-8 text-sm"
        />
      </div>
    ) : null;

  // "12" normally; "3 of 12" while a search is active.
  const libraryLabel = trimmedQuery
    ? t('recordingsPage.libraryCount', { visible: visibleCount, total: list.length })
    : `${list.length}`;

  if (wide) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader description={t('recordingsPage.pageDescription')} />
        <div className="px-6 pt-4">
          <PendingUploadsBanner />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <aside className="flex w-[380px] shrink-0 flex-col border-r border-border">
            <div className="flex items-center justify-between gap-3 px-4 pt-3">
              {/* Clicking the header returns to the upload/record panel (clears the
                  selected recording) — the way "home" from a transcript detail. */}
              <button
                type="button"
                onClick={() => {
                  if (selectedId) clear();
                }}
                disabled={!selectedId}
                aria-label={
                  selectedId
                    ? t('recordingsPage.backToUpload')
                    : t('recordingsPage.libraryAria')
                }
                title={selectedId ? t('recordingsPage.showUploadPanelTitle') : undefined}
                className="rounded-sm text-left font-serif text-lg font-medium text-foreground transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:text-foreground"
              >
                {t('recordingsPage.libraryHeading', { count: libraryLabel })}
              </button>
              <div className="flex items-center gap-2">
                {selectedId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => clear()}
                    aria-label={t('recordingsPage.newRecordingPanelLabel')}
                  >
                    <span aria-hidden className="mr-1">
                      {t('recordingsPage.plusIcon')}
                    </span>{' '}
                    {t('recordingsPage.new')}
                  </Button>
                )}
                <ViewToggle value={view} onChange={setView} />
              </div>
            </div>
            {searchBox && <div className="px-4 pt-3">{searchBox}</div>}
            <div className="min-w-0 flex-1 overflow-auto px-4 pb-6 pt-3">
              <RecordingList
                view={view}
                query={query}
                selectedId={selectedId ?? undefined}
                onSelect={(rec) => select(rec.recordingId)}
                onScrollToUpload={undefined}
              />
            </div>
          </aside>
          <section
            className="min-w-0 flex-1 overflow-auto p-6"
            aria-label={t('recordingsPage.recordingDetailLabel')}
          >
            {selected ? (
              <RecordingPlayer recording={selected} onClose={clear} query={query} />
            ) : (
              <RecordingUpload />
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader description={t('recordingsPage.pageDescription')} />
      <div className="px-4 pt-4">
        <PendingUploadsBanner />
      </div>
      <div className="flex items-center gap-2 px-4 pt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setSelectedId(null);
            setUploadOpen(true);
          }}
        >
          <span aria-hidden className="mr-1">
            {t('recordingsPage.plusIcon')}
          </span>{' '}
          {t('recordingsPage.newRecording')}
        </Button>
        <ViewToggle value={view} onChange={setView} />
      </div>
      {searchBox && <div className="px-4 pt-3">{searchBox}</div>}
      <div className="min-w-0 flex-1 overflow-auto px-4 pb-6 pt-3">
        <RecordingList
          view={view}
          query={query}
          selectedId={selectedId ?? undefined}
          onSelect={(rec) => select(rec.recordingId)}
          onScrollToUpload={() => {
            setSelectedId(null);
            setUploadOpen(true);
          }}
        />
      </div>
      <Drawer
        open={!!selected || uploadOpen}
        onClose={closeDrawer}
        ariaLabel={
          selected
            ? t('recordingsPage.recordingDetailLabel')
            : t('recordingsPage.newRecording')
        }
      >
        {uploadOpen ? (
          <RecordingUpload />
        ) : selected ? (
          <RecordingPlayer recording={selected} onClose={closeDrawer} query={query} />
        ) : null}
      </Drawer>
    </div>
  );
}

export default function RecordingsPage({ initialId }: { initialId?: string | undefined } = {}) {
  const pathname = usePathname();
  return (
    <ErrorBoundary
      FallbackComponent={FeatureErrorFallback}
      onError={handleBoundaryError}
      resetKeys={[pathname]}
    >
      <RecordingsPageInner initialId={initialId} />
    </ErrorBoundary>
  );
}
